import { BATH_DAI_CHANGE_CONFIRM, BATHE_MANAGEMENT } from '@core/constants/text.common';

/**
 * Basic-days pattern helpers shared by the change-confirmation flow and its dialog. Kept apart
 * from the flow so the dialog component can format without importing the flow that opens it.
 */

const DAYS_OF_WEEK_MON_FIRST = ['月', '火', '水', '木', '金', '土', '日'];
const EVERY_WEEK = new Set(['', '00000', '11111']);

/**
 * The pattern as the server's placement rule sees it (BathScheduleRule.Normalize): days trimmed
 * to '' when absent; weeks folded to '' when absent, '00000', '11111', or when the office is
 * nyusho (weeks take no part in placement there). Two patterns that place the dai on the same
 * dates compare equal, so a weeks-only edit in nyusho is not a change.
 */
export function normalizeBathPattern(weeks: string | null | undefined, days: string | null | undefined, isNyuSho: boolean): { weeks: string; days: string } {
    const w = (weeks ?? '').trim();
    const d = (days ?? '').trim();
    return {
        weeks: isNyuSho || EVERY_WEEK.has(w) ? '' : w,
        // All-zero days schedule the dai on nothing, exactly like an absent value: fold both to ''
        // so a freshly-registered dai (basic days unset) does not read as changed against this
        // dialog's '0000000' default. Mirrors BathScheduleRule.Normalize on the server.
        days: /^0+$/.test(d) ? '' : d
    };
}

/** '20241007' → '2024/10/07（月）'. An unparseable value is shown as is. */
export function formatYmdWithDay(ymd: string): string {
    if (!ymd || ymd.length !== 8) return ymd;
    const y = Number(ymd.substring(0, 4));
    const m = Number(ymd.substring(4, 6));
    const d = Number(ymd.substring(6, 8));
    const date = new Date(y, m - 1, d);
    if (isNaN(date.getTime())) return ymd;
    const dow = BATHE_MANAGEMENT.DAYS_JP[date.getDay()];
    return `${ymd.substring(0, 4)}/${ymd.substring(4, 6)}/${ymd.substring(6, 8)}（${dow}）`;
}

/**
 * Label of a pattern for the dialog: the scheduled days joined ('月水'), prefixed for tsusho by
 * the weeks when they are not every week ('第1・3週 月水'). Empty when no day is scheduled.
 */
export function formatBathPatternLabel(weeks: string | null | undefined, days: string | null | undefined, isNyuSho: boolean): string {
    const normalized = normalizeBathPattern(weeks, days, isNyuSho);
    const dayLabel = Array.from(normalized.days)
        .slice(0, 7)
        .map((c, i) => (c === '1' ? DAYS_OF_WEEK_MON_FIRST[i] : ''))
        .join('');
    if (!normalized.weeks) return dayLabel;

    const weekNumbers = Array.from(normalized.weeks)
        .slice(0, 5)
        .map((c, i) => (c === '1' ? String(i + 1) : ''))
        .filter(n => n !== '');
    if (weekNumbers.length === 0) return dayLabel;

    const weekLabel = `${BATH_DAI_CHANGE_CONFIRM.WEEK_PREFIX}${weekNumbers.join(BATH_DAI_CHANGE_CONFIRM.WEEK_SEPARATOR)}${BATH_DAI_CHANGE_CONFIRM.WEEK_SUFFIX}`;
    return dayLabel ? `${weekLabel} ${dayLabel}` : weekLabel;
}
